import { redirect } from "react-router";
import { getAvailableTenants } from "~/hooks/use-available-tenants";
import { authService } from "./auth-service";
import { getFallbackTenantId } from "./tenant-middleware";

/**
 * Middleware for /t routes (without tenant ID)
 *
 * Behavior:
 * - /t: Redirect if localStorage tenant or single tenant exists
 * - /t/*: Redirect if localStorage tenant or single tenant exists
 * - Multiple tenants without localStorage: Allow access, show "select tenant" message
 *
 * Uses tenant priority: localStorage > single default
 */
export const tenantDashboardRedirectMiddleware = async ({
  request,
}: {
  request: Request;
}) => {
  const user = authService.user();

  // If no user, let auth middleware handle it
  if (!user?.teams) {
    return null;
  }

  const url = new URL(request.url);

  // Don't redirect if there's an error parameter (from access denied)
  if (url.searchParams.has("error")) {
    return null;
  }

  const accessibleTenants = getAvailableTenants(user.teams);

  const fallbackTenantId = getFallbackTenantId(accessibleTenants);

  // Note: Tenant context is already set by auth-middleware
  // No need to clear it here - it's already null for /t

  if (fallbackTenantId) {
    // Redirect to tenant-scoped routes if we have a fallback tenant
    if (url.pathname === "/t" || url.pathname === "/t/") {
      // /t → /t/{tenantId}
      throw redirect(`/t/${fallbackTenantId}${url.search}`);
    } else {
      // /t/* → /t/{tenantId}/*
      const newPath = url.pathname.replace("/t/", `/t/${fallbackTenantId}/`);
      throw redirect(`${newPath}${url.search}`);
    }
  }

  // No fallback tenant (multiple tenants, no localStorage)
  // Allow access - component will show "select tenant" message
  return null;
};
